import type { UPIData, UPIScannerOptions } from "./types";
import { parseUPIScheme } from "./upi";

/**
 * Framework-agnostic UPI QR scanner.
 *
 * Works in any browser environment — vanilla JS, React, Vue, Svelte, etc.
 * Dynamically loads `html5-qrcode` on first use.
 *
 * @example
 * ```ts
 * const scanner = new UPIScanner({
 *   container: "#scanner",
 *   onScan: (upi) => console.log(upi.upiId),
 *   onError: (err) => alert(err.message),
 * });
 * await scanner.start();
 * // later...
 * await scanner.stop();
 * ```
 */
export class UPIScanner {
  private options: Required<UPIScannerOptions>;
  private scanner: { stop: () => Promise<void> } | null = null;
  private lastScan = 0;
  private _started = false;
  private containerEl: HTMLElement | null = null;

  constructor(options: UPIScannerOptions) {
    this.options = {
      onError: () => {},
      width: 320,
      height: 320,
      fps: 10,
      debounceMs: 2000,
      ...options,
    };
  }

  private resolveContainer(): HTMLElement | null {
    const { container } = this.options;
    if (typeof container === "string") {
      return document.querySelector(container);
    }
    return container;
  }

  /**
   * Start the camera and begin scanning for UPI QR codes.
   */
  async start(): Promise<void> {
    if (this._started) return;

    this.containerEl = this.resolveContainer();
    if (!this.containerEl) {
      const err = new Error(`UPIScanner: container not found`);
      this.options.onError(err);
      throw err;
    }

    try {
      const { Html5Qrcode } = await import("html5-qrcode");

      // Ensure the container has an ID for html5-qrcode
      let scannerId = this.containerEl.id;
      if (!scannerId) {
        scannerId = `vouch-scanner-${Math.random().toString(36).slice(2, 9)}`;
        this.containerEl.id = scannerId;
      }

      // Clear any previous content
      this.containerEl.innerHTML = "";
      this.containerEl.style.width = `${this.options.width}px`;
      this.containerEl.style.height = `${this.options.height}px`;

      const scanner = new Html5Qrcode(scannerId);
      this.scanner = scanner;

      const qrboxSize = Math.min(this.options.width, this.options.height) * 0.7;

      await scanner.start(
        { facingMode: "environment" },
        { fps: this.options.fps, qrbox: { width: qrboxSize, height: qrboxSize } },
        (decodedText: string) => {
          this.handleScan(decodedText);
        },
        () => {
          // QR not found in this frame — no-op
        }
      );

      this._started = true;
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      this.options.onError(error);
      throw error;
    }
  }

  /**
   * Stop the camera and release resources.
   */
  async stop(): Promise<void> {
    if (!this._started || !this.scanner) return;
    try {
      await this.scanner.stop();
    } catch {
      // ignore cleanup errors
    }
    this.scanner = null;
    this._started = false;
  }

  /** Whether the scanner is currently active. */
  isStarted(): boolean {
    return this._started;
  }

  private handleScan(decodedText: string): void {
    const now = Date.now();
    if (now - this.lastScan < this.options.debounceMs) return;

    const parsed = parseUPIScheme(decodedText);
    if (!parsed) return; // ignore non-UPI codes

    this.lastScan = now;
    this.options.onScan(parsed);
  }
}
